import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useConsole, useRead } from './context';
import { Confirm, DeliveryPill, Empty, Failure, Loading, Section, When } from './bits';
import { Pager } from './Fleet';
import { DeliveryStatus } from '../wire';
import type { WebhookEndpoint } from '../wire';

/**
 * Where a tenant's events go, and what happened to them.
 *
 * Two things this screen must not become, both of which it would be easy to
 * drift into:
 *
 *  * **A place a secret is typed.** `secret_ref` is a Secret Manager resource
 *    name; `apps/notifications` resolves it at call time and the signing secret
 *    is never a column, never a request field, and never in a browser. The
 *    server refuses a value that looks like a secret — a `ys_live_…` string, or
 *    64 hex characters — and the field says so before the click rather than
 *    after it.
 *  * **A payload viewer.** The delivery log carries no payload, because
 *    `admin-api` omits it: it is the one field on this surface that can contain
 *    a guest's name and arrival time, and spec §6.3's line about a data browser
 *    has no exception for JSON. What is here instead is everything an operator
 *    needs to fix a delivery — status, attempts, the next attempt, the far
 *    end's status code and its error text.
 *
 * The replay (spec §5.6.2) is routine rather than heroic: `events` is
 * append-only and complete, and the partner de-duplicates by `booking_id`, so
 * replaying more than was lost is safe by construction. `since` is required —
 * a replay with no bound is every event a tenant has ever emitted.
 */
export function TenantWebhooks({
  tenantId,
  endpoints,
  onChanged,
}: {
  tenantId: string;
  endpoints: WebhookEndpoint[];
  onChanged: () => void;
}): ReactNode {
  return (
    <>
      <Endpoints tenantId={tenantId} endpoints={endpoints} onChanged={onChanged} />
      <Deliveries tenantId={tenantId} onChanged={onChanged} />
    </>
  );
}

function Endpoints({
  tenantId,
  endpoints,
  onChanged,
}: {
  tenantId: string;
  endpoints: WebhookEndpoint[];
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const action = useAction();
  const [editing, setEditing] = useState(false);
  const [integration, setIntegration] = useState('aiconcierge');
  const [url, setUrl] = useState('');
  const [secretRef, setSecretRef] = useState('');
  const [signatureHeader, setSignatureHeader] = useState('X-Provider-Signature');
  const [eventTypes, setEventTypes] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const types = eventTypes
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '');
    const ok = await action.run(async () => {
      const answer = await api.setWebhook(tenantId, {
        integration,
        url,
        secret_ref: secretRef,
        signature_header: signatureHeader,
        event_types: types,
      });
      setNote(`${answer.created ? 'Created' : 'Updated'} ${answer.integration} → ${answer.url}.`);
    });
    if (ok) {
      setEditing(false);
      onChanged();
    }
  };

  return (
    <Section
      title="Webhook endpoints"
      note="Destinations are operator-set, never caller-set (spec §10.13). https only, except http on localhost — the signature proves the payload came from us, it does not hide the guest's name from the network."
      actions={
        <button
          type="button"
          className="button button--primary"
          onClick={() => {
            action.clear();
            setEditing((v) => !v);
          }}
        >
          Set endpoint
        </button>
      }
    >
      {note !== null ? <p className="ok">{note}</p> : null}
      {!editing && action.failure !== null ? <Failure failure={action.failure} /> : null}

      {editing ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="form__row">
            <label className="field">
              <span className="field__label">Integration</span>
              <input value={integration} onChange={(e) => setIntegration(e.target.value)} />
            </label>
            <label className="field field--wide">
              <span className="field__label">URL</span>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://partner.example/hooks/yourstore"
              />
            </label>
          </div>
          <div className="form__row">
            <label className="field field--wide">
              <span className="field__label">Secret reference</span>
              <input
                value={secretRef}
                onChange={(e) => setSecretRef(e.target.value)}
                placeholder="projects/yourstore-dev/secrets/partner-webhook-secret/versions/latest"
              />
              <span className="field__hint">
                A Secret Manager resource name — <strong>not the secret</strong>. The signing secret
                is never stored in the database (spec §7.1) and this field refuses one that looks
                like it.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Signature header</span>
              <input value={signatureHeader} onChange={(e) => setSignatureHeader(e.target.value)} />
            </label>
          </div>
          <label className="field field--wide">
            <span className="field__label">Event types</span>
            <input
              value={eventTypes}
              onChange={(e) => setEventTypes(e.target.value)}
              placeholder="booking.created, booking.cancelled — empty means every type"
            />
          </label>

          {action.failure !== null ? <Failure failure={action.failure} /> : null}

          <div className="row-actions">
            <button type="button" className="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="submit" className="button button--primary" disabled={action.busy}>
              {action.busy ? 'Saving…' : 'Save endpoint'}
            </button>
          </div>
        </form>
      ) : null}

      {endpoints.length === 0 ? (
        <Empty>No endpoint — booking events are recorded but not sent.</Empty>
      ) : (
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Integration</th>
              <th>URL</th>
              <th>Header</th>
              <th>Events</th>
              <th>Secret ref</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((e) => (
              <tr key={e.endpoint_id}>
                <td>{e.integration}</td>
                <td className="wrap">{e.url}</td>
                <td>
                  <code>{e.signature_header}</code>
                </td>
                <td className="muted">
                  {e.event_types.length === 0 ? 'every type' : e.event_types.join(', ')}
                </td>
                <td className="muted wrap">{e.secret_ref}</td>
                <td>
                  {e.active ? 'active' : 'inactive'}
                  {e.failing ? <span className="flag flag--bad">failing</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function Deliveries({
  tenantId,
  onChanged,
}: {
  tenantId: string;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const { config } = useConsole();
  const requeue = useAction();

  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [since, setSince] = useState('');
  const [asking, setAsking] = useState(false);
  const [replayNote, setReplayNote] = useState<string | null>(null);

  const page = useRead(
    () =>
      api.deliveries(tenantId, {
        limit: config.pageSize,
        offset,
        status: status === '' ? undefined : status,
      }),
    [tenantId, status, offset, config.pageSize],
  );

  const confirmRequeue = async (): Promise<void> => {
    const ok = await requeue.run(async () => {
      // `datetime-local` gives a local wall clock with no zone. Sending it raw
      // would make the bound mean something different in St Thomas than on the
      // laptop; the server wants ISO-8601 and gets it, converted once, here.
      const parsed = new Date(since);
      const answer = await api.requeue(
        tenantId,
        Number.isNaN(parsed.getTime()) ? since : parsed.toISOString(),
      );
      setReplayNote(
        `Requeued ${answer.requeued} deliveries (${answer.created} newly created) since ` +
          `${answer.since}; ${answer.endpoints_cleared} endpoint(s) cleared of the failing flag. ` +
          answer.note,
      );
    });
    if (ok) {
      setAsking(false);
      page.reload();
      onChanged();
    }
  };

  return (
    <Section
      title="Delivery log"
      note="At-least-once, de-duplicated by the partner (agreement §6.4). The payload is deliberately not here — it is the one field that could carry a guest's name."
      actions={
        <div className="row-actions">
          <label className="inline-field">
            <span>Status</span>
            <select
              value={status}
              onChange={(e) => {
                setOffset(0);
                setStatus(e.target.value);
              }}
            >
              <option value="">any</option>
              {DeliveryStatus.options.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-field">
            <span>Replay since</span>
            <input
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              aria-label="Replay since"
            />
          </label>
          <button
            type="button"
            className="button"
            disabled={since === ''}
            onClick={() => {
              requeue.clear();
              setAsking(true);
            }}
          >
            Requeue since
          </button>
        </div>
      }
    >
      {replayNote !== null ? <p className="ok">{replayNote}</p> : null}
      {!asking && requeue.failure !== null ? <Failure failure={requeue.failure} /> : null}
      {page.failure !== null ? <Failure failure={page.failure} /> : null}

      {page.value === null ? (
        page.loading ? (
          <Loading what="the delivery log" />
        ) : null
      ) : page.value.deliveries.length === 0 ? (
        <Empty>Nothing delivered or queued{status === '' ? '' : ` with status ${status}`}.</Empty>
      ) : (
        <>
          <table className="table table--dense">
            <thead>
              <tr>
                <th>Occurred</th>
                <th>Event</th>
                <th>Integration</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Next attempt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {page.value.deliveries.map((d) => (
                <Fragment key={d.delivery_id}>
                  <tr>
                    <td>
                      <When iso={d.occurred_at} />
                    </td>
                    <td>
                      <code>{d.event_type}</code>
                    </td>
                    <td>{d.integration}</td>
                    <td>
                      <DeliveryPill status={d.status} />
                    </td>
                    <td>{d.attempts}</td>
                    <td>
                      <When iso={d.next_attempt_at} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="linkish"
                        onClick={() => setOpen(open === d.delivery_id ? null : d.delivery_id)}
                      >
                        {open === d.delivery_id ? 'Hide' : 'Attempt detail'}
                      </button>
                    </td>
                  </tr>
                  {open === d.delivery_id ? (
                    <tr className="detail-row">
                      <td colSpan={7}>
                        <dl className="facts">
                          <div>
                            <dt>Delivery</dt>
                            <dd>
                              <code>{d.delivery_id}</code>
                            </dd>
                          </div>
                          <div>
                            <dt>Event</dt>
                            <dd>
                              <code>{d.event_id}</code>
                            </dd>
                          </div>
                          <div>
                            <dt>URL</dt>
                            <dd className="wrap">{d.url}</dd>
                          </div>
                          <div>
                            <dt>Last status code</dt>
                            <dd>{d.last_status_code ?? '—'}</dd>
                          </div>
                          <div>
                            <dt>Delivered</dt>
                            <dd>
                              <When iso={d.delivered_at} empty="not yet" />
                            </dd>
                          </div>
                          <div>
                            <dt>Updated</dt>
                            <dd>
                              <When iso={d.updated_at} />
                            </dd>
                          </div>
                        </dl>
                        {d.last_error === null ? null : (
                          <pre className="error-text">{d.last_error}</pre>
                        )}
                        <p className="muted">
                          The payload is not shown and is not on the wire: spec §6.3 has no
                          exception for JSON.
                        </p>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>

          <Pager
            count={page.value.deliveries.length}
            limit={page.value.limit}
            offset={page.value.offset}
            busy={page.loading}
            onOffset={setOffset}
          />
        </>
      )}

      {asking ? (
        <Confirm
          title={`Replay every event for ${tenantId} since ${since}?`}
          consequence={
            'Every event since that moment is delivered again, attempts reset to zero, and the ' +
            'endpoints’ failing flags are cleared — a replay is you saying you believe the far ' +
            'end is fixed. The partner de-duplicates by booking_id, so replaying more than was ' +
            'lost is safe; replaying from too far back is a load event of its own.'
          }
          confirmLabel="Requeue"
          busy={requeue.busy}
          failure={requeue.failure}
          onConfirm={() => void confirmRequeue()}
          onCancel={() => {
            requeue.clear();
            setAsking(false);
          }}
        />
      ) : null}
    </Section>
  );
}
